/**
 * Projection for `RolesScene` — the demo's "Who uses what" tab
 * (`ROLEGUIDE` at demo line 3875, rendered at demo line 3934).
 *
 * Six cards, one per role in the VIEWING AS picker. Each names what the role
 * owns, the five pages they actually open, and their typical morning. The
 * `go` on every page button was an inline `show(...)` string, carried as an
 * `OemAction` by the seed and resolved here through `oem-routes.ts`.
 *
 * ## The one id mismatch, handled once
 *
 * `seed/roles.ts` calls the Aftermarket Manager `'after'`; `OemPersonaContext`
 * calls the same person `'aftermarket'`. The seed records the correspondence
 * as `AFTERMARKET_ROLE_TO_PERSONA`; {@link personaForRole} is the only place
 * the port acts on it, so "View as this persona" cannot drift from the picker.
 *
 * Pure: no React, no fetching. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/help/RolesScene.tsx
 * @see docs/logic/help/roles.md
 */

import type { OemPersonaId } from '../../shared/OemPersonaContext';
import { routeForView } from '../../shared/oem-routes';
import {
  AFTERMARKET_ROLE_TO_PERSONA,
  ROLE,
  ROLEGUIDE,
  type RoleId,
} from '../../shared/seed/roles';

/** One page button on a role card. */
export interface RolePage {
  readonly title: string;
  readonly question: string;
  readonly to: string | null;
}

/** One role card. */
export interface RoleCard {
  readonly id: RoleId;
  readonly name: string;
  /** Initials — the demo's avatar. */
  readonly initials: string;
  readonly org: string;
  readonly what: string;
  /** Accent, straight off the seed. Data, so it is an inline style. */
  readonly colour: string;
  readonly pages: readonly RolePage[];
  readonly day: string;
  /** The persona id the "View as" button switches to. */
  readonly personaId: OemPersonaId;
}

/**
 * The persona a role maps onto.
 *
 * Five of the six ids are identical between the two vocabularies; the sixth is
 * the aftermarket manager, whose correspondence the seed records.
 */
export function personaForRole(role: RoleId): OemPersonaId {
  return role === AFTERMARKET_ROLE_TO_PERSONA.role
    ? (AFTERMARKET_ROLE_TO_PERSONA.persona as OemPersonaId)
    : (role as OemPersonaId);
}

/** Demo line 3936 — the avatar is the name's initials, capped at two. */
export const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2);

/** Demo line 3934. The six cards, in seed order. */
export function roleCards(): RoleCard[] {
  return ROLEGUIDE.map((guide) => ({
    id: guide.id,
    name: guide.name,
    initials: initialsOf(guide.name),
    org: guide.org,
    what: guide.what,
    colour: ROLE[guide.id].colour,
    pages: guide.pages.map((page) => ({
      title: page.title,
      question: page.question,
      to: routeForView(page.action.view, page.action.sub),
    })),
    day: guide.day,
    personaId: personaForRole(guide.id),
  }));
}

/** One row of the demo's "How work moves between them" card (demo line 3950). */
export interface HandoverRow {
  readonly heading: string;
  readonly body: string;
}

/** Demo line 3950 — verbatim. */
export const HANDOVER: readonly HandoverRow[] = [
  {
    heading: 'A signal becomes a case',
    body: 'Reliability sees the trend, the agent raises it, the service manager owns it.',
  },
  {
    heading: 'A case becomes a promise',
    body: 'Entitlement decides who pays, planning finds the window, parts and people are checked before a date is offered.',
  },
  {
    heading: 'A promise becomes a package',
    body: 'The technician gets the steps, the torques, the isolation and the kit — with an expert one tap away.',
  },
  {
    heading: 'A package becomes a number',
    body: 'Time and parts close the job, the cost lands on the machine and the site, and the customer is asked what they thought.',
  },
  {
    heading: 'A number becomes a rule',
    body: 'The loops turn it into a changed interval, a bulletin, a stock position or a renewal conversation.',
  },
];
